import {
	useQuery,
	useMutation,
	useQueryClient,
	useMutationState
} from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import {
	DONE,
	GenerateCoverLetterApiResponse,
	GenerateResumeApiResponse,
	GetResumeApiResponse,
	GetLatestCoverLetterApiResponse,
	JobDetails,
	JobDetailsApiResponse,
	UpdateCoverLetterApiPayload,
	UpdateCoverLetterApiResponse,
	JobState,
	APPLIED,
	ArchiveOutcome
} from "../../DataModels/DataModels";
import { API_HTTP, resumeAnalyzerApiEndpoints } from "./endpoints";
import { AuthContext } from "../../context/AuthProvider";
import { User } from "firebase/auth";
import ApiInterface from "./api-interface";
import { useServer } from "./useServer";

export interface UseJobInterface extends JobDetails {
	isLoading: boolean; // Whether the job description is currently loading
	updateJobState: (
		newState: JobState,
		reason?: string,
		outcome?: ArchiveOutcome
	) => Promise<void>;
	updateJob: (updates: { company?: string; title?: string }) => void;
	coverIsLoading: boolean;
	updateCoverLetter: (text: string) => Promise<UpdateCoverLetterApiResponse>;
	generateCoverLetter: () => Promise<GenerateCoverLetterApiResponse>;
	resumeIsLoading: boolean;
	generateResume: () => Promise<GenerateResumeApiResponse>;
}

const ONE_WEEK_DURATION_IN_MS = 7 * 24 * 60 * 60 * 1000;

export function useJob(jobId: string) {
	const [rVal, setRVal] = useState<UseJobInterface>();
	const authContext = useContext(AuthContext);
	const server = useServer();
	const user = authContext?.user as User | undefined;
	const userId = user?.uid;
	const queryClient = useQueryClient();

	//Update Coverletter (With Text)
	const updateCoverletterMutation = useMutation({
		mutationFn: (text: string): Promise<UpdateCoverLetterApiResponse> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			const payload: UpdateCoverLetterApiPayload = { coverletterText: text };
			const res = apiClient.put(
				`${resumeAnalyzerApiEndpoints.coverLetter}/${jobId}`,
				payload
			);
			return res;
		},
		onSuccess: (data, text) => {
			queryClient.setQueryData(["coverLetter", jobId], { coverLetter: text });
			queryClient.setQueryData(["job", jobId], (old: JobDetails) => ({
				...old,
				coverLetterFileId: data.coverLetterFileId,
				coverLetter: text
			}));
			queryClient.invalidateQueries({ queryKey: ["history", jobId] });
		}
	});

	//Generate Coverletter
	const generateCoverletterMutation = useMutation({
		mutationFn: async () => {
			return server.GenerateCoverLetter(jobId);
		}
	});

	// Generate Resume
	const generateResumeMutation = useMutation({
		mutationFn: async () => {
			return server.GenerateResume(jobId);
		}
	});

	//Update Job State
	const updateJobStateMutation = useMutation({
		mutationFn: async ({
			newState,
			reason,
			outcome
		}: {
			newState: JobState;
			reason?: string;
			outcome?: ArchiveOutcome;
		}) => {
			console.log(
				`JobState Mutation Called, Job:[${jobId}] from:[${rVal?.state}]--> to:[${newState}]`
			);
			return server.UpdateJobState(
				jobId,
				rVal?.state ?? APPLIED,
				newState,
				reason,
				outcome
			);
		},
		onSuccess: (data) => {
			console.log(`JobState Mutation success`);
		},
		onError: (err) => {
			console.log(err);
		}
	});

	//Step 1. LookUp Job Id on server and cache it.
	const {
		data: jobServerResponse,
		isLoading: jobIsLoading,
		isPending: jobIsPending,
		isError: jobIsError
	} = useQuery({
		queryKey: ["job", jobId],
		queryFn: async (): Promise<JobDetailsApiResponse> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			const res = apiClient.get(resumeAnalyzerApiEndpoints.jobDetails, {
				jobId
			});
			return res;
		},
		staleTime: ONE_WEEK_DURATION_IN_MS,
		enabled: !!userId
	});

	//Step 2. Lookup Coverletter Data and cache it
	const { data: coverLetterServerResponse, isLoading: coverIsLoading } =
		useQuery({
			queryKey: ["coverLetter", jobId],
			queryFn: async (): Promise<GetLatestCoverLetterApiResponse> => {
				const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
				const res = apiClient.get(
					`${resumeAnalyzerApiEndpoints.coverLetter}/${jobId}`
				);
				return res;
			},
			enabled: !!userId
		});

	// Track the status of the cover letter mutation in the useServer hook
	const useServerGenerateCoverLetterMutationState = useMutationState({
		filters: {
			mutationKey: ["cover-letter", "generate"],
			exact: true,
			predicate: ({ state }) => state.variables?.toString() === jobId
		}
	})[0];

	const isGeneratingCoverLetter =
		!!coverLetterServerResponse?.pendingCoverLetterFileId ||
		generateCoverletterMutation.isPending ||
		useServerGenerateCoverLetterMutationState?.status === "pending";

	const { data: resumeServerResponse, isLoading: resumeIsLoading } = useQuery({
		queryKey: ["resume", jobId],
		queryFn: async (): Promise<GetResumeApiResponse> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			const res = apiClient.get(
				`${resumeAnalyzerApiEndpoints.resume}/job/${jobId}`
			);
			return res;
		},
		staleTime: ONE_WEEK_DURATION_IN_MS,
		enabled: !!userId
	});

	// Track the status of the resume mutation in the useServer hook
	const useServerGenerateResumeMutationState = useMutationState({
		filters: {
			mutationKey: ["resume", "generate"],
			exact: true,
			predicate: ({ state }) => state.variables?.toString() === jobId
		}
	})[0];

	const isGeneratingResume =
		resumeIsLoading ||
		generateResumeMutation.isPending ||
		useServerGenerateResumeMutationState?.status === "pending";

	useEffect(() => {
		const job = jobServerResponse?.job;
		setRVal({
			// JobCardData
			id: jobId,
			title: job?.title ?? "",
			creationTimestamp: job?.creationTimestamp ?? "",
			company: job?.company ?? "",
			state: job?.state ?? DONE,
			isError: jobIsError,
			isPending: jobIsPending,
			// JobDetails
			jobDescription: job?.jobDescription ?? "",
			coverLetterFileId: job?.coverLetterFileId ?? 0,
			coverLetter: coverLetterServerResponse?.coverLetter ?? "",
			isGeneratingCoverLetter,
			resume: resumeServerResponse?.resume ?? "",
			isGeneratingResume,
			// UseJobInterface
			isLoading: jobIsLoading,
			updateJobState: (
				newState: JobState,
				reason?: string,
				outcome?: ArchiveOutcome
			) => updateJobStateMutation.mutateAsync({ newState, reason, outcome }),
			updateJob: (updates: { company?: string; title?: string }) =>
				server.UpdateJob(jobId, updates),
			coverIsLoading,
			updateCoverLetter: (text: string) =>
				updateCoverletterMutation.mutateAsync(text),
			generateCoverLetter: () => generateCoverletterMutation.mutateAsync(),
			resumeIsLoading,
			generateResume: () => generateResumeMutation.mutateAsync()
		});

		//NOTE: we don't want to link to the Mutations - that creates an infinite loop
		// eslint-disable-next-line
	}, [
		jobIsLoading,
		jobServerResponse,
		coverIsLoading,
		isGeneratingCoverLetter,
		coverLetterServerResponse,
		generateCoverletterMutation.isPending,
		resumeIsLoading,
		isGeneratingResume,
		resumeServerResponse,
		generateResumeMutation.isPending,
		jobId
	]);

	return rVal;
}
